import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, MessagesSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { getCliente, deleteCliente, activarChatCliente, getApiErrorMessage } from "@/lib/api";
import { nombreVisible } from "@/lib/clienteNombre";
import { ClienteFicha } from "@/components/clientes/ClienteFicha";
import { MiembrosDialog, AvataresApilados } from "@/components/chat/MiembrosDialog";

export default function ClienteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const { refreshChats } = useChat();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activarOpen, setActivarOpen] = useState(false);
  const [borrarOpen, setBorrarOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true });
    else if (user.permissions?.clientes === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getCliente(id)
      .then(setCliente)
      .catch(() => toast.error("No se encontró el cliente"))
      .finally(() => setLoading(false));
  }, [id, user]);

  const activarChat = async (miembros) => {
    try {
      await activarChatCliente(id, miembros);
      await refreshChats();
      navigate(`/chat/${id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo crear el chat"));
      throw err;
    }
  };

  const borrar = async () => {
    try {
      await deleteCliente(id);
      toast.success("Cliente eliminado");
      refreshChats();
      navigate("/clientes");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo eliminar"));
    }
  };

  const soyMiembro = cliente?.miembros?.includes(user?.email);
  const puedeChat = user?.permissions?.chat !== false;

  return (
    <div className="min-h-dvh bg-background">
      <AppMainHeader title="Clientes" titleSuffix={cliente ? ` / ${nombreVisible(cliente)}` : ""} />
      <main className="w-full px-4 md:px-6 py-6 pb-10 max-w-5xl mx-auto">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 mb-4 text-muted-foreground" onClick={() => navigate("/clientes")}>
          <ArrowLeft className="w-4 h-4" /> Clientes
        </Button>
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !cliente ? (
          <p className="text-center py-24 text-sm text-muted-foreground">Cliente no encontrado.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
            <div className="rounded-xl border bg-card p-5 md:p-6">
              <ClienteFicha cliente={cliente} onChange={setCliente} />
            </div>
            <aside className="space-y-4">
              {puedeChat && (
                <div className="rounded-xl border bg-card p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <MessagesSquare className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium">Chat del cliente</h3>
                  </div>
                  {cliente.chat_activo ? (
                    <>
                      <AvataresApilados emails={cliente.miembros} max={6} size={30} />
                      {soyMiembro || user?.permissions?.admin ? (
                        <Button className="w-full gap-2" onClick={() => navigate(`/chat/${cliente.id}`)}>
                          <MessagesSquare className="w-4 h-4" /> Abrir chat
                        </Button>
                      ) : (
                        <p className="text-xs text-muted-foreground">Este chat es solo para sus miembros. Pide a alguno que te añada.</p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">Un espacio para hablar solo de este cliente, con sus trabajos pendientes anclados arriba.</p>
                      <Button className="w-full gap-2" onClick={() => setActivarOpen(true)}>
                        <MessagesSquare className="w-4 h-4" /> Crear chat
                      </Button>
                    </>
                  )}
                </div>
              )}
              {user?.permissions?.admin && (
                <Button variant="ghost" className="w-full gap-2 text-muted-foreground hover:text-destructive" onClick={() => setBorrarOpen(true)}>
                  <Trash2 className="w-4 h-4" /> Eliminar cliente
                </Button>
              )}
            </aside>
          </div>
        )}
      </main>
      <MiembrosDialog
        open={activarOpen}
        onOpenChange={setActivarOpen}
        titulo="¿Quién participa en este chat?"
        descripcion="Solo las personas que elijas verán la conversación."
        fijos={user?.email ? [user.email] : []}
        textoBoton="Crear chat"
        onConfirm={activarChat}
      />
      <AlertDialog open={borrarOpen} onOpenChange={setBorrarOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {cliente ? nombreVisible(cliente) : "el cliente"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrará la ficha, el chat, sus archivos y sus trabajos. No se puede deshacer. Si solo ha dejado de ser cliente, márcalo como Inactivo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
